import { useEffect, useState } from "react";
import { fetchRoles, fetchPromotionCriteria, savePromotionCriteria } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import CertificationCombobox from "./CertificationCombobox";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Award, Info, ShieldCheck } from "lucide-react";

const CRITERIA_TYPES = [
  { value: "min_tenure_years", label: "Min. Kıdem (yıl)" },
  { value: "min_performance_rating", label: "Min. Performans Puanı" },
  { value: "min_engagement_score", label: "Min. Bağlılık Skoru" },
  { value: "min_skill_level", label: "Yetkinlik Seviyesi" },
  { value: "required_certification", label: "Sertifika/Program" },
  { value: "custom", label: "Serbest Kriter" },
];

// Type-specific defaults (label + threshold + placeholder + helper)
const TYPE_META = {
  min_tenure_years:       { label: "Min. Kıdem",              threshold: 2,   placeholder: "Yıl",            helper: "Örn: 3 (yıl)" },
  min_performance_rating: { label: "Min. Performans Puanı",   threshold: 3.5, placeholder: "1.0 – 5.0",      helper: "Örn: 3.5 (son dönem)" },
  min_engagement_score:   { label: "Min. Bağlılık Skoru",     threshold: 60,  placeholder: "0 – 100",        helper: "Örn: 65 (pulse ort.)" },
  min_skill_level:        { label: "Yetkinlik Seviyesi",      threshold: 4,   placeholder: "1 – 5",          helper: "skill_id + 1-5 seviye" },
  required_certification: { label: "Zorunlu Sertifika",       threshold: "",  placeholder: "Sertifika adı",  helper: "Örn: CFA Level 1, PMP, SCRUM Master" },
  custom:                 { label: "Serbest Kriter",          threshold: "",  placeholder: "Değer",          helper: "İK manuel değerlendirir" },
};

export default function PromotionCriteriaEditor() {
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [criteria, setCriteria] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoGen, setAutoGen] = useState(false);

  useEffect(() => {
    fetchRoles().then((r) => setRoles(Array.isArray(r) ? r : (r?.roles || []))).catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    if (!roleId) return;
    setLoading(true);
    fetchPromotionCriteria(roleId)
      .then((d) => {
        setCriteria(d.criteria || []);
        setNotes(d.notes || "");
        setAutoGen(d.auto_generated || false);
      })
      .catch(() => { setCriteria([]); setNotes(""); })
      .finally(() => setLoading(false));
  }, [roleId]);

  const updateCriterion = (i, patch) => {
    const next = [...criteria];
    next[i] = { ...next[i], ...patch };
    setCriteria(next);
  };
  // When user changes type, auto-refresh label + threshold from TYPE_META so the row makes sense for the new criterion
  const changeType = (i, newType) => {
    const meta = TYPE_META[newType] || {};
    const next = [...criteria];
    // Only overwrite label if the previous label was the auto-default of the OLD type (user hasn't customized)
    const prevType = next[i].type;
    const prevMeta = TYPE_META[prevType] || {};
    const labelWasAuto = !next[i].label || next[i].label === prevMeta.label;
    next[i] = {
      ...next[i],
      type: newType,
      label: labelWasAuto ? meta.label : next[i].label,
      threshold: meta.threshold ?? "",
      // Clear skill_id when leaving skill type
      ...(newType !== "min_skill_level" ? { skill_id: undefined } : {}),
    };
    setCriteria(next);
  };
  const addCriterion = (type = "min_tenure_years") => {
    const meta = TYPE_META[type] || TYPE_META.min_tenure_years;
    setCriteria([...criteria, { type, label: meta.label, threshold: meta.threshold, weight: 1, mandatory: true }]);
  };
  const removeCriterion = (i) => setCriteria(criteria.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!roleId) { toast.error("Önce rol seç"); return; }
    setSaving(true);
    try {
      await savePromotionCriteria({ role_id: roleId, criteria, notes });
      toast.success("Terfi kriterleri kaydedildi");
      setAutoGen(false);
    } catch {
      toast.error("Kayıt başarısız");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="promotion-criteria-editor">
      <div className="card-surface p-4">
        <div className="label-eyebrow flex items-center gap-2 mb-2"><Award className="w-3.5 h-3.5 text-amber-600" /> Terfi Kriter Havuzu (YTN-25)</div>
        <div className="text-sm text-slate-600 mb-3">Her rol için terfi kriterlerini yazılı olarak tanımla. Çalışan hedef rolüne başvururken bu kriterlere karşı otomatik değerlendirilir.</div>
        <Select value={roleId} onValueChange={setRoleId}>
          <SelectTrigger className="w-full md:w-96 bg-white" data-testid="promotion-role-select">
            <SelectValue placeholder="Rol seç..." />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.title} {r.career_band ? `(${r.career_band})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {roleId && (
        loading ? (
          <div className="card-surface p-6 flex items-center justify-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>
        ) : (
          <div className="card-surface p-4 space-y-3">
            {autoGen && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded text-xs text-amber-900">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Bu rol için henüz kaydedilmiş kriter yok. Sistem varsayılan bir şablon önerdi — düzenleyip kaydet.
              </div>
            )}
            {criteria.map((c, i) => {
              const meta = TYPE_META[c.type] || TYPE_META.custom;
              return (
              <div key={i} className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-2" data-testid={`promotion-criterion-${i}`}>
                <div className="flex gap-2 flex-wrap items-center">
                  <Select value={c.type} onValueChange={(v) => changeType(i, v)}>
                    <SelectTrigger className="w-56 h-8 bg-white" data-testid={`criterion-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CRITERIA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={c.label} onChange={(e) => updateCriterion(i, { label: e.target.value })} placeholder="Görünen etiket" className="flex-1 min-w-[160px] h-8" data-testid={`criterion-label-${i}`} />
                  {c.type === "required_certification" ? (
                    <div className="w-64">
                      <CertificationCombobox
                        value={c.threshold ?? ""}
                        onChange={(v) => updateCriterion(i, { threshold: v })}
                        testid={`criterion-threshold-${i}`}
                      />
                    </div>
                  ) : (
                    <Input
                      value={c.threshold ?? ""}
                      onChange={(e) => updateCriterion(i, { threshold: e.target.value })}
                      placeholder={meta.placeholder}
                      className="w-44 h-8"
                      data-testid={`criterion-threshold-${i}`}
                      title={meta.helper}
                    />
                  )}
                  <Input type="number" min={1} max={5} value={c.weight ?? 1} onChange={(e) => updateCriterion(i, { weight: parseInt(e.target.value) || 1 })} placeholder="Ağırlık" className="w-16 h-8" title="Ağırlık 1-5" />
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    <input type="checkbox" checked={c.mandatory} onChange={(e) => updateCriterion(i, { mandatory: e.target.checked })} /> Zorunlu
                  </label>
                  {c.type === "min_skill_level" && (
                    <Input value={c.skill_id || ""} onChange={(e) => updateCriterion(i, { skill_id: e.target.value })} placeholder="skill_id" className="w-40 h-8" data-testid={`criterion-skill-${i}`} />
                  )}
                  <Button size="icon" variant="ghost" onClick={() => removeCriterion(i)}><Trash2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                </div>
                <div className="text-[11px] text-slate-500 pl-1 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Info className="w-3 h-3" /> {meta.helper}</span>
                  {(c.source || c.created_by) && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200/70 text-slate-600 text-[10px]" data-testid={`criterion-source-${i}`}>
                      <ShieldCheck className="w-2.5 h-2.5" />
                      {c.source === "rfp-YTN-25" ? "RFP YTN-25" : c.source === "system-default" ? "Sistem varsayılanı" : "İK — Manuel"}
                      {c.created_at && <span className="text-slate-400">· {new Date(c.created_at).toLocaleDateString("tr-TR")}</span>}
                    </span>
                  )}
                </div>
              </div>
              );
            })}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 flex-wrap">
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => addCriterion("min_tenure_years")} data-testid="promotion-add-criterion" className="h-8"><Plus className="w-3.5 h-3.5 mr-1" /> Kriter Ekle</Button>
                <Button size="sm" variant="outline" onClick={() => addCriterion("required_certification")} data-testid="promotion-add-certificate" className="h-8 border-amber-200 text-amber-800 hover:bg-amber-50">
                  <Award className="w-3.5 h-3.5 mr-1" /> Sertifika Ekle
                </Button>
                <Button size="sm" variant="outline" onClick={() => addCriterion("min_skill_level")} className="h-8">+ Yetkinlik</Button>
              </div>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="promotion-save-criteria">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Kaydet
              </Button>
            </div>
            <div>
              <label className="text-xs text-slate-500">Not (opsiyonel)</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Örn: 2026 Q3 kalibrasyonu sonrası güncellendi." />
            </div>
          </div>
        )
      )}
    </div>
  );
}
